import { ThemeEvents } from '@theme/events';
import { Component } from '@theme/component';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';
import { formatMoney } from '@theme/money-formatting';

/**
 * @typedef {Object} ProductPriceRefs
 * @property {HTMLElement} priceContainer
 * @property {HTMLElement} [volumePricingNote]
 */

/**
 * A custom element that displays a product price.
 * This component listens for variant update events and updates the price display accordingly.
 * It handles price updates from two different sources:
 * 1. Variant picker (in quick add modal or product page)
 * 2. Swatches variant picker (in product cards)
 *
 * @extends {Component<ProductPriceRefs>}
 */
class ProductPrice extends Component {
  connectedCallback() {
    super.connectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);
    closestSection.addEventListener('change', this.#handleAddonChange);
    this.#syncAddonAdjustedPrices();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.removeEventListener(StandardEvents.productSelect, this.#handleProductSelect);
    closestSection.removeEventListener('change', this.#handleAddonChange);
  }

  /**
   * Handles product select event and updates the price.
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html, newProduct } = detail;

        if (newProduct) {
          this.dataset.productId = newProduct.id;
        } else if (detail.productId && detail.productId !== this.dataset.productId) {
          return;
        }

        const { priceContainer, volumePricingNote } = this.refs;
        // Find the new product-price element in the updated HTML
        const newProductPrice = html.querySelector(`product-price[data-block-id="${this.dataset.blockId}"]`);
        if (!newProductPrice) return;

        // Update price container
        const newPrice = newProductPrice.querySelector('[ref="priceContainer"]');
        if (newPrice && priceContainer) {
          priceContainer.replaceWith(newPrice);
        }

        // Update volume pricing note
        const newNote = newProductPrice.querySelector('[ref="volumePricingNote"]');
        if (!newNote) {
          volumePricingNote?.remove();
        } else if (!volumePricingNote) {
          // Use newPrice since priceContainer was just replaced and now points to the detached element
          newPrice?.insertAdjacentElement('afterend', /** @type {Element} */ (newNote.cloneNode(true)));
        } else {
          volumePricingNote.replaceWith(newNote);
        }

        // Update installments (SPI banner) variant ID to trigger payment terms re-render
        const installmentsInput = /** @type {HTMLInputElement|null} */ (
          this.querySelector(`#product-form-installment-${this.dataset.blockId} input[name="id"]`)
        );
        if (installmentsInput) {
          installmentsInput.value = detail.resource?.id ?? '';
          installmentsInput.dispatchEvent(new Event('change', { bubbles: true }));
        }

        this.#syncAddonAdjustedPrices();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[product-price] Event promise rejected:', error);
      });
  };

  /** @param {Event} event */
  #handleAddonChange = (event) => {
    if (!(event.target instanceof HTMLInputElement)) return;
    if (!event.target.matches('[data-linked-addon-input]')) return;

    this.#syncAddonAdjustedPrices();
  };

  #getAddonTotals() {
    const productSection = this.closest('.shopify-section, dialog');
    const addonInputs = productSection?.querySelectorAll('[data-linked-addon-input]:checked') || [];

    return Array.from(addonInputs).reduce(
      (totals, inputElement) => {
        const input = /** @type {HTMLInputElement} */ (inputElement);
        totals.price += Number(input.dataset.addonPrice || 0);
        totals.compareAt += Number(input.dataset.addonCompareAtPrice || input.dataset.addonPrice || 0);
        return totals;
      },
      { price: 0, compareAt: 0 }
    );
  }

  #syncAddonAdjustedPrices() {
    const productSection = this.closest('.shopify-section, dialog');
    if (!productSection?.querySelector('.buy-buttons-block--heaven')) return;

    const addonTotals = this.#getAddonTotals();
    const priceContainers = productSection.querySelectorAll('[ref="priceContainer"][data-base-price]');

    priceContainers.forEach((priceContainerElement) => {
      const priceContainer = /** @type {HTMLElement} */ (priceContainerElement);
      const basePrice = Number(priceContainer.dataset.basePrice || 0);
      const baseCompareAt = Number(priceContainer.dataset.baseCompareAtPrice || basePrice || 0);
      const moneyFormat = priceContainer.dataset.moneyFormat || '{{amount}}';
      const currency = priceContainer.dataset.currency || 'USD';
      const basePriceMin = Number(priceContainer.dataset.basePriceMin || basePrice || 0);
      const basePriceMax = Number(priceContainer.dataset.basePriceMax || basePrice || 0);

      const totalPrice = basePrice + addonTotals.price;
      const totalCompareAt = baseCompareAt + addonTotals.compareAt;
      const formattedPrice = formatMoney(totalPrice, moneyFormat, currency);
      const formattedCompareAt = formatMoney(totalCompareAt, moneyFormat, currency);

      const regularPrice = priceContainer.querySelector('.price__regular .price');
      const salePrice = priceContainer.querySelector('.price__sale .price-item--sale.price');
      const comparePrice = priceContainer.querySelector('.price__sale .price-item--regular.compare-at-price');
      const regularWrapper = priceContainer.querySelector('.price__regular');
      const saleWrapper = priceContainer.querySelector('.price__sale');
      const rangePrice = priceContainer.querySelector('.price-item__group .price');

      if (rangePrice && priceContainer.dataset.basePriceMin) {
        const formattedMin = formatMoney(basePriceMin + addonTotals.price, moneyFormat, currency);
        const formattedMax = formatMoney(basePriceMax + addonTotals.price, moneyFormat, currency);
        rangePrice.textContent = `${formattedMin} - ${formattedMax}`;
      }

      if (regularPrice) {
        regularPrice.textContent = formattedPrice;
      }

      if (salePrice) {
        salePrice.textContent = formattedPrice;
      }

      if (comparePrice) {
        comparePrice.textContent = formattedCompareAt;
      }

      const hasSale = totalCompareAt > totalPrice;
      if (regularWrapper && saleWrapper) {
        regularWrapper.classList.toggle('price__hidden', hasSale);
        saleWrapper.classList.toggle('price__hidden', !hasSale);
      }
    });
  }
}

if (!customElements.get('product-price')) {
  customElements.define('product-price', ProductPrice);
}
